import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, finalize, withLatestFrom } from 'rxjs/operators';

import { PrizeRequestsActions } from '../_actions/prize-requests.actions';
import { PrizeRequestsService } from '../../_services/prize-requests.service';
import { LayoutActions } from '../_actions/layout.actions';
import { LayoutUtilsServices, MessageType } from '../../_services/layout-utils.services';
import { AppState } from '../index';
import { currentPrizeRequestsFilters } from '../_selectors/prize-requests.selectors';

@Injectable()
export class PrizeRequestsEffects {
  loadPrizeRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrizeRequestsActions.loadPrizeRequests),
      switchMap(({ filters }) =>
        this.prizeRequestsService.findAll(filters).pipe(
          map((res) =>
            PrizeRequestsActions.loadPrizeRequestsSuccess({
              items: res.data,
              total: res.total,
            })
          ),
          catchError((error) =>
            of(PrizeRequestsActions.loadPrizeRequestsFailure({ error }))
          )
        )
      )
    )
  );

  loadPrizeRequestById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrizeRequestsActions.loadPrizeRequestById),
      switchMap(({ id }) =>
        this.prizeRequestsService.findOne(id).pipe(
          map((item) =>
            PrizeRequestsActions.loadPrizeRequestByIdSuccess({ item })
          ),
          catchError((error) =>
            of(PrizeRequestsActions.loadPrizeRequestByIdFailure({ error }))
          )
        )
      )
    )
  );

  approvePrizeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrizeRequestsActions.approvePrizeRequest),
      withLatestFrom(this.store.select(currentPrizeRequestsFilters)),
      switchMap(([{ id, approveDto }, currentFilters]) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this.prizeRequestsService.approve(id, approveDto).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((item) => {
            this.layoutUtilsService.showNotification({
              message: 'Solicitud aprobada exitosamente',
              type: MessageType.Susses,
            });
            // Recargar lista con los filtros actuales
            this.store.dispatch(
              PrizeRequestsActions.loadPrizeRequests({ filters: currentFilters })
            );
            return PrizeRequestsActions.approvePrizeRequestSuccess({ item });
          }),
          catchError((error) => {
            this.layoutUtilsService.showNotification({
              message: error?.error?.message || 'Error al aprobar la solicitud',
              type: MessageType.Danger,
            });
            return of(PrizeRequestsActions.approvePrizeRequestFailure({ error }));
          })
        );
      })
    )
  );

  rejectPrizeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrizeRequestsActions.rejectPrizeRequest),
      withLatestFrom(this.store.select(currentPrizeRequestsFilters)),
      switchMap(([{ id, rejectDto }, currentFilters]) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this.prizeRequestsService.reject(id, rejectDto).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((item) => {
            this.layoutUtilsService.showNotification({
              message: 'Solicitud rechazada exitosamente',
              type: MessageType.Susses,
            });
            // Recargar lista con los filtros actuales
            this.store.dispatch(
              PrizeRequestsActions.loadPrizeRequests({ filters: currentFilters })
            );
            return PrizeRequestsActions.rejectPrizeRequestSuccess({ item });
          }),
          catchError((error) => {
            this.layoutUtilsService.showNotification({
              message: error?.error?.message || 'Error al rechazar la solicitud',
              type: MessageType.Danger,
            });
            return of(PrizeRequestsActions.rejectPrizeRequestFailure({ error }));
          })
        );
      })
    )
  );

  deliverPrizeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrizeRequestsActions.deliverPrizeRequest),
      withLatestFrom(this.store.select(currentPrizeRequestsFilters)),
      switchMap(([{ id, deliverDto }, currentFilters]) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this.prizeRequestsService.deliver(id, deliverDto).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((item) => {
            this.layoutUtilsService.showNotification({
              message: 'Solicitud marcada como entregada exitosamente',
              type: MessageType.Susses,
            });
            // Recargar lista con los filtros actuales
            this.store.dispatch(
              PrizeRequestsActions.loadPrizeRequests({ filters: currentFilters })
            );
            return PrizeRequestsActions.deliverPrizeRequestSuccess({ item });
          }),
          catchError((error) => {
            this.layoutUtilsService.showNotification({
              message: error?.error?.message || 'Error al marcar como entregada',
              type: MessageType.Danger,
            });
            return of(PrizeRequestsActions.deliverPrizeRequestFailure({ error }));
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private prizeRequestsService: PrizeRequestsService,
    private store: Store<AppState>,
    private layoutUtilsService: LayoutUtilsServices
  ) {}
}
